import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import FunctionsIcon from "@mui/icons-material/Functions";
import LayersClearIcon from "@mui/icons-material/LayersClearOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlined";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import { MilestoneProgressBar, type ProgressMilestone } from "../../../components/UIToolkit/Graphs/MilestoneProgressBar/MilestoneProgressBar";
import { AccentIconTile } from "../../../components/UIToolkit/molecules/AccentIconTile";
import { InfoIconWithTooltip } from "../../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { SystemBanner } from "../../../components/UIToolkit/molecules/SystemBanner";
import type { PbjPlanActionProgress } from "../../../utils/models";
import { PBJ_ALPHA } from "../PredictionTab/colors";
import { RECOMMENDATION_TYPES, recColor } from "../PredictionTab/recommendations.constants";
import {
	formatCurrency,
	formatPlanNumber,
	formatPlanValue,
	formatWholeHours,
	isActionJudged,
	milestonePct,
	type PlanBasis,
	planValueOf,
} from "./planValue.utils";

type Props = {
	action: PbjPlanActionProgress;
	basis: PlanBasis;
	weeksRemaining: number;
};

export const PlanActionCard: FC<Props> = ({ action, basis, weeksRemaining }) => {
	const { t } = useTranslation();
	const rec = RECOMMENDATION_TYPES.find((r) => r.id === action.id);
	const theme = useTheme();
	// Levers carry a theme-derived `colorKey`, not a baked hex, so the tile re-tints with the org's brand.
	const accent = rec ? recColor(theme, rec.colorKey) : undefined;
	const unit = t(basis === "hours" ? "pbj_model.tracker.unit_hours" : "pbj_model.tracker.unit_hppd");
	const delivered = planValueOf(action.delivered, basis);
	// Both sides of the on-track comparison are charged over completed weeks, so before the first one
	// closes the verdict is vacuously true and is withheld rather than shown green. `behind` follows the
	// same gate: a lever that cannot be judged yet has not been found wanting either.
	const judged = isActionJudged(action.days);
	const behind = action.feasible && judged && !action.onTrack;
	// Rounded to whole hours for the chips: these are context figures read at a glance beside the
	// measured ones, and a decimal invites them to be reconciled against numbers they do not sum to.
	//
	// Coalesced and compared against zero rather than tested with `!== null`. These fields are
	// optional by contract — absent means "nothing to show" — and `undefined !== null` is true, so a
	// strict null check renders the chip and then formats `undefined`, taking the whole tab down with
	// it. A tracker that goes blank because one optional number is missing is a far worse failure
	// than a chip that does not appear, and the two are indistinguishable to a reader either way.
	const manualHours = action.manualHours ?? 0;
	const estimatedHours = action.estimatedHours ?? 0;
	const beforePlanManual = action.manualHoursBeforePlan ?? 0;
	const alreadyBookedManual = action.manualHoursAlreadyBooked ?? 0;

	// Markers sit on the same 0–100 axis as `progressPct` — both are a share of the committed lift.
	// Points come from CROSSING a band edge, so each marker is a discrete step; nothing is
	// interpolated between them.
	//
	// Both the position and the achieved test are charged in HOURS, against the very figure the marker
	// prints. Reported HPPD normalises over the plan window while an edge's gap is the lift required
	// across the whole quarter, so it squeezed every marker leftward by `plan_days / quarter_days` —
	// which put edges BEHIND an EOQ projection that had not reached them, flatly contradicting the
	// points the projection was credited.
	const milestones: ProgressMilestone[] = action.milestones.map((milestone, index) => ({
		id: `${milestone.categoryId}-${milestone.star}-${index}`,
		pct: milestonePct(milestone.hours, action.goalTotal.hours),
		label: t("pbj_model.tracker.milestone_points", { value: milestone.points.toFixed(0) }),
		sublabel: `${formatPlanNumber(basis === "hours" ? milestone.hours : milestone.hppdGap, basis)} ${unit}`,
		achieved: action.deliveredToDate.hours >= milestone.hours,
		tooltip: t("pbj_model.tracker.milestone_tooltip", { star: milestone.star, points: milestone.points.toFixed(0) }),
	}));
	// A band edge costing more than the whole commitment can't be reached by this plan. Saying so
	// beats silently dropping it off the right end of the bar.
	const beyondPlan = milestones.filter((m) => m.pct > 100).length;

	// Where the pace set so far lands at quarter end. Omitted outright when the engine has no pace to
	// extend — a plan saved today, or a weekend lever before its first weekend — rather than drawn at
	// zero, which would forecast total failure on a quarter nobody has had the chance to work. The
	// truthiness check also covers the field being absent altogether against an older backend, for the
	// same reason the manual-hours figures above are coalesced.
	const projection = action.projected
		? {
				pct: action.projected.pct,
				label: t("pbj_model.tracker.projected_on_bar", { value: `${formatPlanValue(action.projected.delivered, basis)} ${unit}` }),
				reachesTarget: action.projected.meetsGoal,
				tooltip: t("pbj_model.tracker.projection_tooltip"),
			}
		: undefined;
	// The one withheld-forecast case worth explaining: a measurable lever that has had no day it can be
	// worked on yet, which is a weekend one before its first weekend. It resolves by itself within days,
	// so the reader is promised something. An INFEASIBLE lever is deliberately excluded — its own chip
	// already says it cannot be measured at all, and nothing is coming.
	const projectionPending = !action.projected && action.feasible && action.days.elapsed === 0;

	// The committed rate is quoted per WEEK so it is comparable to `updatedGoal`, which sits beside it
	// behind an arrow and is per week by construction.
	//
	// DEFAULT: falls back to the per-day rate when the weekly twin is absent. Flagged per repo
	// standards. The weekly figure is optional by contract for the same reason the manual-hours fields
	// above are — an older backend does not ship it, and formatting `undefined` would blank the tab.
	// The suffix follows the value rather than being pinned to "week", so the fallback is labelled for
	// what it actually is instead of passing a per-day figure off as a weekly one.
	const committedRate = action.goalRateWeekly ?? action.goalRate;
	const committedRateSuffix = t(action.goalRateWeekly ? "pbj_model.tracker.per_week_suffix" : "pbj_model.tracker.per_day_suffix");

	return (
		<Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }} data-testid={`pbj-model-plan-action-${action.id}`}>
			<Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ justifyContent: "space-between", alignItems: { md: "center" }, mb: 2 }}>
				<Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0 }}>
					{rec && accent && (
						<AccentIconTile size="xs" color={accent} tint={alpha(accent, PBJ_ALPHA.TINT)}>
							{rec.icon}
						</AccentIconTile>
					)}
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
							{rec ? t(rec.titleKey) : action.id}
						</Typography>
						<Stack direction="row" spacing={0.75} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5, mt: 0.5 }}>
							{/* An infeasible lever is neither on nor off track — it cannot be measured at all,
							    so it gets its own read rather than a red "behind" badge over 0%. */}
							{!action.feasible ? (
								<MetaChip
									tone="warning"
									icon={<BlockIcon />}
									label={t("pbj_model.tracker.infeasible")}
									tooltip={t("pbj_model.tracker.infeasible_tooltip")}
									dataTestId={`pbj-model-plan-action-status-${action.id}`}
								/>
							) : judged ? (
								<MetaChip
									tone={action.onTrack ? "success" : "error"}
									icon={action.onTrack ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
									label={t(action.onTrack ? "pbj_model.tracker.on_track" : "pbj_model.tracker.behind")}
									dataTestId={`pbj-model-plan-action-status-${action.id}`}
								/>
							) : (
								// Vacuously true, so withheld — see `isActionJudged`. This is what stops "On track"
								// appearing beside a forecast that lands thousands of hours short of the goal.
								<MetaChip
									tone="neutral"
									icon={<RemoveCircleOutlineIcon />}
									label={t("pbj_model.tracker.not_judged_yet")}
									tooltip={t("pbj_model.tracker.not_judged_yet_tooltip")}
									dataTestId={`pbj-model-plan-action-status-${action.id}`}
								/>
							)}
							{/* `delivered` counts COMPLETED weeks only — it is what the badge above is judged
							    on, and differs from the bar (every elapsed day) by up to six days of hours.
							    The ⓘ carries that explanation now that the per-card date line is gone. */}
							<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
								<Typography variant="caption" color="text.secondary" data-testid={`pbj-model-plan-action-delivered-${action.id}`}>
									{t("pbj_model.tracker.total_added", { value: `${formatPlanValue(action.delivered, basis)} ${unit}` })}
								</Typography>
								<InfoIconWithTooltip
									title={t("pbj_model.tracker.delivered_tooltip", { covered: action.days.covered, elapsed: action.days.elapsed })}
									iconSx={{ fontSize: 13 }}
									dataTestId={`pbj-model-plan-action-delivered-info-${action.id}`}
								/>
							</Box>
							{/* `goalPoints`, NOT the stored `points`. The stored figure is the marginal gain the
							    Action Plan credited at save time, scored over the Prediction tab's window and
							    frozen; this one is the live ladder walk the markers on this very bar are drawn
							    from. Showing the frozen one here put "0 pts committed" beside a ladder whose
							    cheapest edge the commitment clears — read as a bug, not as two measurements. */}
							<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
								<Typography variant="caption" color="text.disabled" data-testid={`pbj-model-plan-action-points-${action.id}`}>
									{t("pbj_model.tracker.action_points", { value: action.goalPoints.toFixed(0) })}
								</Typography>
								<InfoIconWithTooltip
									title={t("pbj_model.tracker.action_points_tooltip")}
									iconSx={{ fontSize: 13 }}
									dataTestId={`pbj-model-plan-action-points-info-${action.id}`}
								/>
							</Box>
							{/* Per-lever spend has no home in the mock, but the endpoint ships it and the
							    plan-level panel only totals it — keeping it here preserves the breakdown. */}
							<Typography variant="caption" color="text.disabled" data-testid={`pbj-model-plan-action-spend-${action.id}`}>
								{t("pbj_model.tracker.action_spend", { spent: formatCurrency(action.spendSoFar), price: formatCurrency(action.price) })}
							</Typography>
						</Stack>
						{/* Manual and estimated hours get their own row, deliberately NOT the one above.
						    They are gross HOUR LEVELS measured quarter-to-date, while everything above is
						    delivery measured since the plan was saved — and a level sitting inches from a
						    delta reads as a contribution to it. A department whose manual hours predate
						    its plan sees a large figure here beside "Total added: 0", which is correct and
						    was impossible to tell apart when the two shared a line. */}
						{(manualHours > 0 || estimatedHours > 0 || alreadyBookedManual > 0) && (
							<Stack direction="row" spacing={0.75} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5, mt: 0.75 }}>
								{manualHours > 0 && (
									<MetaChip
										tone="info"
										icon={<EditNoteIcon />}
										// The pre-plan share is named on the chip itself, not buried in the tooltip:
										// it is the whole reason a four-figure manual total can sit beside a
										// delivered figure of zero, and a reader who has to hover to discover that
										// has already read the two as contradicting each other.
										label={
											beforePlanManual > 0
												? t("pbj_model.tracker.includes_manual_split", {
														value: formatWholeHours(manualHours),
														before: formatWholeHours(beforePlanManual),
													})
												: t("pbj_model.tracker.includes_manual", { value: formatWholeHours(manualHours) })
										}
										tooltip={t("pbj_model.tracker.includes_manual_tooltip")}
										dataTestId={`pbj-model-plan-action-manual-${action.id}`}
									/>
								)}
								{/* Entered but not counted. Without this the user sees the tab hold still after a
								    write and cannot tell "already counted from the Audit tab" from "my entry did
								    not register" — the second reading is what sent them looking for a bug. */}
								{alreadyBookedManual > 0 && (
									<MetaChip
										tone="warning"
										icon={<LayersClearIcon />}
										label={t("pbj_model.tracker.manual_already_booked", { value: formatWholeHours(alreadyBookedManual) })}
										tooltip={t("pbj_model.tracker.manual_already_booked_tooltip")}
										dataTestId={`pbj-model-plan-action-manual-dup-${action.id}`}
									/>
								)}
								{estimatedHours > 0 && (
									<MetaChip
										tone="info"
										icon={<FunctionsIcon />}
										label={t("pbj_model.tracker.includes_estimate", { value: formatWholeHours(estimatedHours) })}
										tooltip={t("pbj_model.tracker.includes_estimate_tooltip")}
										dataTestId={`pbj-model-plan-action-estimate-${action.id}`}
									/>
								)}
							</Stack>
						)}
					</Box>
				</Stack>

				{/* Committed rate, and the raised rate needed to recover when behind. */}
				<Stack
					direction="row"
					spacing={2}
					sx={(theme) => ({
						alignItems: "center",
						flexShrink: 0,
						px: 2,
						py: 1,
						borderRadius: 1.5,
						border: `1px solid ${theme.palette.divider}`,
						bgcolor: alpha(theme.palette.text.primary, 0.02),
					})}
				>
					<Box>
						<Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600 }}>
							{t("pbj_model.tracker.goal_rate")}
						</Typography>
						<Typography variant="subtitle2" sx={{ fontWeight: 700 }} data-testid={`pbj-model-plan-action-goal-${action.id}`}>
							{`${formatPlanValue(committedRate, basis)} ${unit}${committedRateSuffix}`}
						</Typography>
					</Box>
					{action.updatedGoal !== null && behind && (
						<>
							<ArrowForwardIcon sx={{ fontSize: 16, color: "text.disabled" }} />
							<Box>
								<Typography variant="caption" color="error.main" sx={{ display: "block", fontWeight: 600 }}>
									{t("pbj_model.tracker.updated_goal")}
								</Typography>
								<Typography variant="subtitle2" sx={{ fontWeight: 700, color: "error.main" }} data-testid={`pbj-model-plan-action-updated-goal-${action.id}`}>
									{`${formatPlanValue(action.updatedGoal, basis)} ${unit}${t("pbj_model.tracker.per_week_suffix")}`}
								</Typography>
							</Box>
						</>
					)}
				</Stack>
			</Stack>

			{/* Why the lever reads behind, in the plan's own numbers, with the way back. */}
			{behind && (
				<SystemBanner
					tone="error"
					icon={<ErrorOutlineIcon />}
					title={t("pbj_model.tracker.behind_title")}
					description={
						action.updatedGoal !== null
							? t("pbj_model.tracker.behind_body_recover", {
									required: `${formatPlanValue(action.requiredToDate, basis)} ${unit}`,
									delivered: `${formatPlanValue(action.delivered, basis)} ${unit}`,
									catchup: `${formatPlanValue(action.updatedGoal, basis)} ${unit}${t("pbj_model.tracker.per_week_suffix")}`,
								})
							: t("pbj_model.tracker.behind_body_no_weeks", {
									required: `${formatPlanValue(action.requiredToDate, basis)} ${unit}`,
									delivered: `${formatPlanValue(action.delivered, basis)} ${unit}`,
								})
					}
					sx={{ mb: 2 }}
					dataTestId={`pbj-model-plan-action-behind-${action.id}`}
				/>
			)}

			{/* The bar fills to `deliveredToDate` (every elapsed day), NOT to `delivered`. The value
			    label rides the end of the fill so progress reads against the coloured line itself. */}
			<MilestoneProgressBar
				value={action.progressPct}
				milestones={milestones}
				projection={projection}
				valueLabel={t("pbj_model.tracker.delivered_on_bar", { value: `${formatPlanValue(action.deliveredToDate, basis)} ${unit}` })}
				tone={action.feasible ? (action.onTrack ? "primary" : "warning") : "warning"}
				ariaLabel={t("pbj_model.tracker.action_progress_aria", { action: rec ? t(rec.titleKey) : action.id })}
				dataTestId={`pbj-model-plan-action-bar-${action.id}`}
			/>
			<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 1 }}>
				<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
					{t("pbj_model.tracker.baseline_label")}
				</Typography>
				<Typography variant="caption" sx={{ fontWeight: 700 }} data-testid={`pbj-model-plan-action-pct-${action.id}`}>
					{t("pbj_model.tracker.percent_complete", { value: action.progressPct.toFixed(0) })}
				</Typography>
				{/* The commitment, bottom-right — the number the whole bar is filling toward. */}
				<Stack direction="row" spacing={0.5} sx={{ alignItems: "center", color: "text.primary" }}>
					<TrackChangesIcon sx={{ fontSize: 14 }} />
					<Typography variant="caption" sx={{ fontWeight: 700 }} data-testid={`pbj-model-plan-action-goal-total-${action.id}`}>
						{t("pbj_model.tracker.goal_on_bar", { value: `${formatPlanValue(action.goalTotal, basis)} ${unit}` })}
					</Typography>
				</Stack>
			</Stack>
			{/* The projection in words, because the marker alone cannot say what the landing spot is worth.
			    Points come from CROSSING a band edge, so a projection short of the goal can still be worth
			    the full points of every edge it clears — and one that all but reaches the goal can be worth
			    nothing. Naming the points is the only way the reader learns which of the two they have. */}
			{action.projected && (
				<Stack direction="row" spacing={0.75} sx={{ alignItems: "flex-start", mt: 1 }} data-testid={`pbj-model-plan-action-pace-${action.id}`}>
					<TrendingUpIcon sx={{ fontSize: 14, color: "text.disabled", mt: 0.25, flexShrink: 0 }} />
					<Typography variant="caption" color="text.secondary">
						{action.projected.meetsGoal
							? t("pbj_model.tracker.pace_meets_goal", {
									value: `${formatPlanValue(action.projected.delivered, basis)} ${unit}`,
									points: action.projected.points.toFixed(0),
								})
							: t("pbj_model.tracker.pace_short", {
									value: `${formatPlanValue(action.projected.delivered, basis)} ${unit}`,
									shortfall: `${formatPlanValue(action.projected.shortfall, basis)} ${unit}`,
									points: action.projected.points.toFixed(0),
								})}
					</Typography>
				</Stack>
			)}
			{/* The forecast is withheld through week 0 rather than extrapolated off a single day. Saying so
			    beats letting the marker simply not be there: its absence is indistinguishable from a bug,
			    and this is the one stretch of the plan where a reader has no other way to find out. */}
			{projectionPending && (
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", fontStyle: "italic", mt: 0.75 }}
					data-testid={`pbj-model-plan-action-pace-pending-${action.id}`}
				>
					{t("pbj_model.tracker.projection_pending_note")}
				</Typography>
			)}
			{(beyondPlan > 0 || (action.feasible && weeksRemaining === 0) || delivered < 0) && (
				<Stack spacing={0.25} sx={{ mt: 0.75 }}>
					{delivered < 0 && (
						<Typography variant="caption" color="error.main" sx={{ fontStyle: "italic" }}>
							{t("pbj_model.tracker.below_baseline_note")}
						</Typography>
					)}
					{beyondPlan > 0 && (
						<Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
							{t("pbj_model.tracker.milestones_beyond_plan", { count: beyondPlan })}
						</Typography>
					)}
					{action.feasible && weeksRemaining === 0 && (
						<Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
							{t("pbj_model.tracker.quarter_complete_note")}
						</Typography>
					)}
				</Stack>
			)}
		</Paper>
	);
};
